import { useEffect } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle,
  AlertCircle,
  RotateCcw,
  Download,
  ArrowRight,
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import TopNav from "../components/TopNav";
import certificateTemplate from "../assets/certificate-template.jpeg";
import { useGetExamResultQuery } from "../redux/Clinictrainingapi";

/* ════════════════════════════════════════════════════════════
   CERTIFICATE COMPONENT
   Renders the JPEG template with dynamic text overlaid using
   absolute percentage positioning — stays proportional at any
   rendered width. The Download button bakes both onto a canvas.
════════════════════════════════════════════════════════════ */
function Certificate({ certData }) {
  const recipientName  = certData?.first_name && certData?.last_name
    ? `${certData.first_name} ${certData.last_name}`
    : certData?.user ?? "—";
  const instructorName = certData?.instructor_name  ?? "—";
  const certificateId  = certData?.certificate_id   ?? "";
  const issuedDate     = certData?.issued_date
    ? new Date(certData.issued_date).toLocaleDateString("en-US", {
        month: "long", day: "numeric", year: "numeric",
      })
    : new Date().toLocaleDateString("en-US", {
        month: "long", day: "numeric", year: "numeric",
      });

  /* Canvas download — merges template + text into a single PNG */
  const handleDownload = () => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = certificateTemplate;
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width  = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      const W = canvas.width;
      const H = canvas.height;

      ctx.drawImage(img, 0, 0);

      // Recipient name — centred, ABOVE horizontal line ~35%, calligraphy script
      ctx.textAlign   = "center";
      ctx.fillStyle   = "#1a1a2e";
      ctx.font        = `${Math.round(W * 0.06)}px 'Great Vibes', cursive`;
      ctx.fillText(recipientName, W / 2, H * 0.39);

      // Instructor name — centred, ABOVE signature line ~78%, calligraphy script
      ctx.font        = `${Math.round(W * 0.032)}px 'Great Vibes', cursive`;
      ctx.fillStyle   = "#1a1a2e";
      ctx.fillText(instructorName, W / 2, H * 0.80);

      // Issued date — after "Issued:" label with spacing
      ctx.textAlign   = "left";
      ctx.font        = `${Math.round(W * 0.016)}px Georgia, serif`;
      ctx.fillStyle   = "#333";
      ctx.fillText(issuedDate, W * 0.27, H * 0.918);

      // Certificate ID — after "Certificate ID:" label with spacing
      if (certificateId) {
        ctx.font        = `${Math.round(W * 0.016)}px Georgia, serif`;
        ctx.fillStyle   = "#333";
        ctx.textAlign   = "left";
        ctx.fillText(certificateId, W * 0.745, H * 0.918);
      }

      const link = document.createElement("a");
      link.download = `HIPAA-Certificate-${recipientName.replace(/\s+/g, "-")}.png`;
      link.href     = canvas.toDataURL("image/png");
      link.click();
    };
  };

  return (
    /* Outer wrapper keeps the image's aspect ratio and positions children */
    <div className="relative w-full select-none" style={{ userSelect: "none" }}>

      {/* ── Base template image ── */}
      <img
        src={certificateTemplate}
        alt="HIPAA Compliance Certificate"
        className="w-full h-auto rounded-lg block"
        draggable={false}
      />

      {/* ── Text overlay — percentage positions match the template layout ── */}
      <div className="absolute inset-0 rounded-lg overflow-hidden pointer-events-none">

        {/* Recipient name — ABOVE the horizontal line, calligraphy script */}
        <div className="absolute left-0 right-0 flex justify-center" style={{ top: "27%", height: "16%" }}>
          <span
            className="text-center leading-tight"
            style={{
              color: "#1a1a2e",
              fontSize: "clamp(18px, 5vw, 58px)",
              fontFamily: "'Great Vibes', cursive",
              fontWeight: 400,
              maxWidth: "75%",
              wordBreak: "break-word",
            }}
          >
            {recipientName}
          </span>
        </div>

        {/* Instructor name — ABOVE the signature line, same calligraphy script */}
        <div className="absolute left-0 right-0 flex justify-center" style={{ top: "76.5%" }}>
          <span
            className="text-center"
            style={{
              color: "#1a1a2e",
              fontSize: "clamp(11px, 2.2vw, 26px)",
              fontFamily: "'Great Vibes', cursive",
              fontWeight: 400,
            }}
          >
            {instructorName}
          </span>
        </div>

        {/* Bottom row values — same baseline as template labels */}
        <div className="absolute left-[27%]" style={{ bottom: "5.8%" }}>
          <span style={{ color: "#333", fontSize: "clamp(6px, 1.05vw, 13px)", fontFamily: "Georgia, serif", lineHeight: 1 }}>
            {issuedDate}
          </span>
        </div>

        {certificateId && (
          <div className="absolute left-[74.5%]" style={{ bottom: "5.8%" }}>
            <span style={{ color: "#333", fontSize: "clamp(6px, 1.05vw, 13px)", fontFamily: "Georgia, serif", lineHeight: 1 }}>
              {certificateId}
            </span>
          </div>
        )}
      </div>

      {/* ── Download button — sits at bottom of certificate ── */}
      <div className="absolute bottom-3 sm:bottom-5 left-0 right-0 flex justify-center pointer-events-auto">
        <button
          onClick={handleDownload}
          className="flex items-center gap-2 bg-gradient-to-r from-primary to-cyan-400 hover:from-primary-700 hover:to-primary text-white px-4 sm:px-6 py-2 sm:py-2.5 rounded-full font-semibold text-xs sm:text-sm transition-all shadow-lg shadow-primary/25"
        >
          <Download size={14} />
          Download Certificate
        </button>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   MAIN PAGE
════════════════════════════════════════════════════════════ */
export default function ExamResults() {
  const navigate  = useNavigate();
  const location  = useLocation();

  // Always scroll to top
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const hasState = !!location.state;

  const {
    score        = 0,
    totalQuestions = 0,
    correctAnswers = 0,
    timeSpent    = 0,
    isPassed     = false,
    passingScore = 80,
  } = location.state ?? {};

  // Always fetch certificate — needed for both returning users and pass view
  const { data: certList, isLoading: certLoading } = useGetExamResultQuery();
  const certData = Array.isArray(certList) ? certList[0] : certList;

  // If user came directly (no location.state), determine pass/fail from API
  const passed = hasState
    ? (isPassed ?? score >= passingScore)
    : !!certData;

  // If no state AND no certificate from API (and done loading), redirect to dashboard
  if (!hasState && !certLoading && !certData) {
    return <Navigate to="/dashboard" replace />;
  }

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const issuedDisplay = certData?.issued_date
    ? new Date(certData.issued_date).toLocaleDateString("en-US", {
        month: "short", day: "numeric", year: "numeric",
      })
    : new Date().toLocaleDateString("en-US", {
        month: "short", day: "numeric", year: "numeric",
      });

  /* ── PASS VIEW ─────────────────────────────────────────── */
  if (passed) {
    return (
      <div className="min-h-screen bg-surface">
        <Sidebar />
        <div className="ml-0 md:ml-16">
          <TopNav
            backTo="/dashboard"
            backLabel="BACK TO CURRICULUM"
            breadcrumbs={["HIPAA COMPLIANCE TRAINING"]}
            rightContent={
              <span className="hidden sm:flex items-center gap-1.5 text-xs text-green-600 font-medium">
                <CheckCircle size={14} />
                Status: Certified
              </span>
            }
          />

          <div className="flex justify-center px-4 sm:px-6 py-6 sm:py-8">
            <motion.div
              className="w-full max-w-2xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              {/* Success header */}
              <div className="text-center mb-6 sm:mb-8">
                <motion.div
                  className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-green-100 mx-auto mb-4 flex items-center justify-center"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <CheckCircle size={24} className="text-green-500 sm:hidden" />
                  <CheckCircle size={28} className="text-green-500 hidden sm:block" />
                </motion.div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                  Congratulations{certData?.first_name ? `, ${certData.first_name}` : ""}!
                </h1>
                <p className="text-gray-500 text-sm max-w-md mx-auto px-2">
                  You have successfully completed NexaLife HIPAA Compliance
                  Certification Training. Your certificate is ready below.
                </p>
              </div>

              {/* Stats cards — 1 col mobile, 3 col sm+ */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8">
                <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5 text-center">
                  <div className="text-xs text-gray-400 uppercase tracking-wider font-medium mb-2">
                    Your Score
                  </div>
                  <div className="text-3xl font-bold text-primary">{score}%</div>
                  <div className="text-xs text-primary mt-1">Passing: {passingScore}%</div>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5 text-center">
                  <div className="text-xs text-gray-400 uppercase tracking-wider font-medium mb-2">
                    Time Spent
                  </div>
                  <div className="text-3xl font-bold text-gray-900">{formatTime(timeSpent)}</div>
                  <div className="text-xs text-gray-400 mt-1">Average: 18:00</div>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5 text-center">
                  <div className="text-xs text-gray-400 uppercase tracking-wider font-medium mb-2">
                    Issued
                  </div>
                  <div className="text-base sm:text-lg font-bold text-gray-900">{issuedDisplay}</div>
                  {certData?.certificate_id && (
                    <div className="text-xs text-gray-400 mt-1 font-mono truncate px-1" title={certData.certificate_id}>
                      {certData.certificate_id}
                    </div>
                  )}
                </div>
              </div>

              {/* Certificate preview */}
              <motion.div
                className="bg-white rounded-2xl border border-gray-200 p-3 sm:p-4 mb-6 sm:mb-8 shadow-sm"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                {certLoading ? (
                  <div className="flex items-center justify-center h-48 text-gray-400 text-sm gap-2">
                    <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    Preparing certificate…
                  </div>
                ) : (
                  <Certificate certData={certData} />
                )}
              </motion.div>

              {/* Back link */}
              <div className="flex justify-center pb-4">
                <button
                  onClick={() => navigate("/dashboard")}
                  className="flex items-center gap-2 text-primary hover:text-primary-700 font-medium text-sm transition-colors cursor-pointer"
                >
                  Back to Dashboard
                  <ArrowRight size={16} />
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    );
  }

  /* ── FAIL VIEW ─────────────────────────────────────────── */
  return (
    <div className="min-h-screen bg-surface">
      <Sidebar />
      <div className="ml-0 md:ml-16">
        <TopNav
          backTo="/dashboard"
          backLabel="BACK TO CURRICULUM"
          breadcrumbs={["COMPLIANCE TRAINING", "EXAM RESULTS"]}
          rightContent={
            <span className="hidden sm:flex items-center gap-1.5 text-xs text-primary font-medium">
              <CheckCircle size={14} />
              HIPAA Certification
            </span>
          }
        />

        <div className="flex justify-center px-4 sm:px-6 py-6 sm:py-8">
          <motion.div
            className="w-full max-w-xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Header */}
            <div className="text-center mb-6 sm:mb-8">
              <div className="text-xs font-bold text-primary tracking-widest uppercase mb-2">
                Compliance Certification
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">
                Exam Results
              </h1>
              <p className="text-sm text-gray-400">HIPAA Security &amp; Privacy Standards</p>
            </div>

            {/* Score card */}
            <motion.div
              className="bg-white rounded-2xl border border-gray-200 p-6 sm:p-10 text-center mb-6 sm:mb-8"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.2 }}
            >
              <div className="w-32 h-32 sm:w-40 sm:h-40 mx-auto mb-5 sm:mb-6 relative">
                <svg className="w-full h-full -rotate-90" viewBox="0 0 160 160">
                  <circle cx="80" cy="80" r="70" fill="none" stroke="#FEE2E2" strokeWidth="8" />
                  <motion.circle
                    cx="80" cy="80" r="70"
                    fill="none" stroke="#EF4444" strokeWidth="8" strokeLinecap="round"
                    strokeDasharray={440}
                    initial={{ strokeDashoffset: 440 }}
                    animate={{ strokeDashoffset: 440 - (440 * score) / 100 }}
                    transition={{ duration: 1, delay: 0.4, ease: "easeOut" }}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-3xl sm:text-4xl font-bold text-gray-900">{score}%</span>
                  <span className="text-xs text-gray-400 uppercase tracking-wider font-medium">Total Score</span>
                </div>
              </div>

              <div className="inline-flex items-center gap-2 bg-red-50 text-red-500 px-4 py-2 rounded-full text-sm font-medium mb-5 sm:mb-6">
                <AlertCircle size={16} />
                Certification Not Achieved
              </div>

              <h2 className="text-base sm:text-lg font-bold text-gray-900 mb-2">
                You did not meet the passing score
              </h2>
              <p className="text-sm text-gray-400 max-w-sm mx-auto mb-6 sm:mb-8 px-2">
                The minimum passing score is {passingScore}%. Please review the
                modules and retake the exam.
              </p>

              <button
                onClick={() => navigate("/final-exam")}
                className="flex items-center gap-2 bg-gradient-to-r from-primary to-cyan-400 hover:from-primary-700 hover:to-primary text-white px-8 sm:px-10 py-3.5 rounded-full font-semibold text-sm transition-all cursor-pointer shadow-lg shadow-primary/25 mx-auto w-full sm:w-auto justify-center"
              >
                <RotateCcw size={16} />
                Retake Exam
              </button>
            </motion.div>

            {/* Stats cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 pb-4">
              <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5 text-center">
                <div className="text-xs text-primary uppercase tracking-wider font-medium mb-2">Questions</div>
                <div className="text-xl sm:text-2xl font-bold text-gray-900">{totalQuestions} Total</div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5 text-center">
                <div className="text-xs text-primary uppercase tracking-wider font-medium mb-2">Correct</div>
                <div className="text-xl sm:text-2xl font-bold text-gray-900">
                  {correctAnswers}{" "}
                  <span className="text-sm text-gray-400 font-normal">/ {totalQuestions}</span>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5 text-center">
                <div className="text-xs text-primary uppercase tracking-wider font-medium mb-2">Time Taken</div>
                <div className="text-xl sm:text-2xl font-bold text-gray-900">{formatTime(timeSpent)}</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}